import { useState, useRef, useEffect } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';
import { suggestMapping, applyMapping } from '../lib/bulkImportFields';
import { MAX_FILE_BYTES, parseCsv, readWorkbook, rowsFromSheet } from '../lib/migrationFiles';
import { downloadFailedRows } from '../lib/productImportReport';

const initial = { step: 'welcome', file: null, parsing: false, headers: [], rows: [], mapping: {}, previewLoading: false, preview: null, duplicateAction: 'skip', importing: false, result: null, savedMapping: null, rememberMapping: true, profiles: [], profileName: 'Default mapping', sheetNames: [], sheetName: '', fileError: '', confirmedUpdates: {} };
const errorMessage = (error, fallback) => typeof error.response?.data?.detail === 'string' ? error.response.data.detail : fallback;

export function useMigrationWizard(kind, config) {
  const [state, setState] = useState(initial);
  const generation = useRef(0);
  const workbook = useRef(null);
  const patch = fields => setState(s => ({ ...s, ...fields }));
  useEffect(() => () => { generation.current++; workbook.current = null; }, []);
  const startOver = () => { generation.current++; workbook.current = null; setState(initial); };

  const acceptRows = async (data, ticket) => {
    let profiles = [];
    try {
      const response = await api.post(`/migrations/${kind}/mappings/match`, { headers: data.headers });
      profiles = response.data.profiles;
    } catch { toast.warning('Saved mappings are unavailable. You can still match columns manually.'); }
    if (ticket !== generation.current) return;
    const savedMapping = profiles[0] || null;
    patch({ ...data, profiles, savedMapping, profileName: savedMapping?.name || 'Default mapping', mapping: savedMapping?.mapping || suggestMapping(data.headers, config.fields), parsing: false });
  };

  const selectSheet = async (sheetName) => {
    const ticket = ++generation.current;
    patch({ sheetName, headers: [], rows: [], parsing: true, preview: null, result: null, fileError: '', savedMapping: null, confirmedUpdates: {} });
    try { await acceptRows(rowsFromSheet(workbook.current, sheetName), ticket); }
    catch (error) { if (ticket === generation.current) patch({ parsing: false, fileError: error.message }); }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const ticket = ++generation.current;
    workbook.current = null;
    patch({ ...initial, step: 'upload', file, parsing: true });
    try {
      if (!/\.(csv|xlsx|xls)$/i.test(file.name)) throw new Error('Choose a CSV, XLSX or XLS file.');
      if (file.size > MAX_FILE_BYTES) throw new Error('Files must be 10 MB or smaller.');
      if (/\.csv$/i.test(file.name)) await acceptRows(await parseCsv(file), ticket);
      else {
        const book = await readWorkbook(file);
        if (ticket !== generation.current) return;
        workbook.current = book;
        patch({ sheetNames: book.SheetNames, parsing: false });
        if (book.SheetNames.length === 1) await selectSheet(book.SheetNames[0]);
      }
    } catch (error) { if (ticket === generation.current) patch({ parsing: false, fileError: error.message }); }
  };

  const chooseProfile = (value) => {
    const profile = state.profiles.find(p => (p.profile_id || 'legacy') === value);
    patch({ savedMapping: profile || null, profileName: profile?.name || '', ...(profile ? { mapping: profile.mapping } : {}) });
  };
  const forgetProfile = async () => {
    try {
      await api.delete(`/migrations/${kind}/mappings/${state.savedMapping.profile_id}`);
      patch({ profiles: state.profiles.filter(p => p.profile_id !== state.savedMapping.profile_id), savedMapping: null, profileName: 'Default mapping' });
      toast.success('Mapping forgotten');
    } catch (error) { toast.error(errorMessage(error, 'Could not forget this mapping')); }
  };

  const runPreview = async () => {
    patch({ step: 'preview', previewLoading: true, preview: null, confirmedUpdates: {} });
    try {
      const response = await api.post(`${config.endpoint}/preview`, { rows: applyMapping(state.rows, state.mapping) });
      patch({ preview: response.data });
      if (state.rememberMapping) {
        try {
          const response = await api.put(`/migrations/${kind}/mappings`, { headers: state.headers, mapping: state.mapping, name: state.profileName, profile_id: state.savedMapping?.profile_id || null });
          patch({ savedMapping: response.data, profiles: [response.data, ...state.profiles.filter(p => p.profile_id !== response.data.profile_id && p.profile_id)] });
        } catch (error) { toast.warning(errorMessage(error, 'Preview is ready, but the mapping could not be saved.')); }
      }
      patch({ previewLoading: false });
    } catch (error) { toast.error(errorMessage(error, 'Could not preview this file')); patch({ step: 'map', previewLoading: false }); }
  };

  const runImport = async () => {
    patch({ step: 'results', importing: true, result: null });
    try {
      const response = await api.post(`${config.endpoint}/execute`, { rows: applyMapping(state.rows, state.mapping), duplicate_action: state.duplicateAction, confirmed_updates: state.confirmedUpdates });
      patch({ result: response.data, importing: false });
      if (response.data.imported + response.data.updated > 0) toast.success(`${response.data.imported} imported, ${response.data.updated} updated`);
      else toast.info('No records changed. Review the results below.');
    } catch (error) { toast.error(errorMessage(error, 'Import failed')); patch({ step: 'duplicates', importing: false }); }
  };

  const exportFailures = async failures => {
    try {
      if (kind !== 'products') await api.post(`/migrations/${kind}/report-export`, { row_count: failures.length });
      downloadFailedRows(state.headers, state.rows, failures, state.file?.name.replace(/\.(xlsx|xls)$/i, `-${state.sheetName}.csv`));
    } catch { toast.error('Could not prepare the report. Please try again.'); }
  };
  return { state, patch, startOver, selectSheet, handleFileSelect, chooseProfile, forgetProfile, runPreview, runImport, exportFailures };
}